import { zodResolver } from "@hookform/resolvers/zod";
import { BaseButton } from "@shared/components/atoms/BaseButton/BaseButton";
import { BaseInput } from "@shared/components/atoms/BaseInput/BaseInput";
import { FormField } from "@shared/components/molecules/FormField/FormField";
import { handleApiError } from "@shared/lib/handleApiError";
import { Zap } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import {
	type LoginFormValues,
	loginSchema,
} from "../../domain/schemas";
import { useIsAuthenticated, useLogin } from "../hooks/useAuth";
import "./LoginPage.scss";

function LoginPage() {
	// Selectores de Zustand: re-renders solo cuando cambia lo que nos importa.
	const login = useLogin();
	const isAuthenticated = useIsAuthenticated();

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
	});

	if (isAuthenticated) {
		return <Navigate to="/dashboard" replace />;
	}

	const onSubmit = async (data: LoginFormValues) => {
		try {
			await login(data.email, data.password);
		} catch (err: unknown) {
			// El store ya guardó el mensaje en `lastError`. Mostramos el
			// toast estándar (kind/código del backend) y un fallback en el
			// form por si la UI quiere recuperarlo después.
			handleApiError(err);
			const msg =
				(err as { message?: string })?.message || "Error al iniciar sesión";
			setError("root", { message: msg });
		}
	};

	return (
		<div className="login">
			<div className="login__brand">
				<Zap size={18} />
				<span>Whatmix</span>
			</div>

			<div className="login__card">
				<h1 className="login__title">Iniciar sesión</h1>
				<p className="login__subtitle">
					Accede a tu workspace para gestionar tus conversaciones.
				</p>

				<form className="login__form" onSubmit={handleSubmit(onSubmit)}>
					{errors.root && (
						<div className="login__error">{errors.root.message}</div>
					)}

					<FormField label="Email" required error={errors.email?.message}>
						<BaseInput
							type="email"
							placeholder="admin@localhost"
							hasError={!!errors.email}
							autoComplete="email"
							autoFocus
							{...register("email")}
						/>
					</FormField>

					<FormField
						label="Contraseña"
						required
						error={errors.password?.message}
					>
						<BaseInput
							type="password"
							placeholder="••••••••"
							hasError={!!errors.password}
							autoComplete="current-password"
							{...register("password")}
						/>
					</FormField>

					<BaseButton
						type="submit"
						variant="primary"
						disabled={isSubmitting}
						className="login__submit"
					>
						{isSubmitting ? "Ingresando..." : "Iniciar sesión"}
					</BaseButton>
				</form>

				{/*
				 * Link a la pantalla de recuperación. Si la ruta aún no existe
				 * (no implementada), el navegador mostrará 404 — comportamiento
				 * explícito y honesto, mejor que un botón muerto.
				 */}
				<Link to="/forgot-password" className="login__forgot">
					¿Olvidaste tu contraseña?
				</Link>
			</div>
		</div>
	);
}

export default LoginPage;